import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Building2, Shield, 
  Edit, X, Plus, Trash2, ChevronDown, Tag, Users, Mail, Phone, Globe, MapPin, Droplets
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import dataroomService from '../services/dataroomService';
import CommonLoader from '../components/CommonLoader';
import { CreateButton, UpdateButton, DeleteButton } from '../components/CommonButtons';
import DataroomTags from '../components/DataroomTags';
import DataroomRoles from '../components/DataroomRoles';
import DataroomMembers from '../components/DataroomMembers';
import DataroomMembersAdmin from '../components/DataroomMembersAdmin';
import DataroomInvitations from '../components/DataroomInvitations';
import { useAuth } from '../contexts/AuthContext';

const DataroomDetail = () => {
  const { user } = useAuth();
  const { uuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [dataroom, setDataroom] = useState(null);
  
  // Get active tab from URL params, default to 'details'
  const activeTab = searchParams.get('tab') || 'details';
  
  // Actions dropdown state
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [showWatermarkDropdown, setShowWatermarkDropdown] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [watermarkLoading, setWatermarkLoading] = useState(false);
  
  // Region selection for activation
  const [showRegionModal, setShowRegionModal] = useState(false);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');

  // Function to change tab
  const handleTabChange = (tabId) => {
    setSearchParams({ tab: tabId });
  };

  useEffect(() => {
    fetchDataroomDetails();
    fetchRegions();
  }, [uuid]);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionsDropdown && !event.target.closest('.actions-dropdown')) {
        setShowActionsDropdown(false);
      }
      if (showWatermarkDropdown && !event.target.closest('.watermark-dropdown')) {
        setShowWatermarkDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActionsDropdown, showWatermarkDropdown]);

  const fetchDataroomDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/datarooms/${uuid}`);
      setDataroom(response.data.data || response.data);
    } catch (error) {
      toast.error('Failed to load dataroom details');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await dataroomService.getAWSRegions();
      setRegions(response.data || []);
    } catch (error) {
      // Don't show error toast as regions are not critical for initial load
    }
  };

  const confirmRegionAndActivate = async () => {
    if (!selectedRegion) {
      toast.error('Please select a region');
      return;
    }

    try {
      setActionLoading(true);
      setShowRegionModal(false);
      
      await dataroomService.activateDataroom(uuid, selectedRegion);
      toast.success('Dataroom activated successfully');
      
      setSelectedRegion('');
      fetchDataroomDetails();
    } catch (error) {
      
      if (error.response?.data?.message?.includes('already') || error.response?.data?.message?.includes('approved')) {
        toast.info('Dataroom is already active');
        fetchDataroomDetails();
      } else {
        toast.error(error.response?.data?.message || 'Failed to activate dataroom');
      }
    } finally {
      setActionLoading(false);
    }
  };




  const handleWatermarkToggle = async (enabled) => {
    setShowWatermarkDropdown(false);
    setWatermarkLoading(true);
    
    try {
      const response = await api.post(`/admin/datarooms/${uuid}/watermark/toggle`, {
        enabled: enabled
      });
      
      toast.success(`Watermark ${enabled ? 'enabled' : 'disabled'} successfully`);
      
      // Update local dataroom state
      setDataroom(prev => ({
        ...prev,
        watermark_enabled: enabled ? 1 : 0
      }));
    } catch (error) {
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to update watermark setting');
      }
    } finally {
      setWatermarkLoading(false);
    }
  };

  const handleDataroomAction = async (action) => {
    setShowActionsDropdown(false);
    
    try {
      switch (action) {
        case 'activate':
          // Check if dataroom has a region, if not show modal to select one
          if (!dataroom.region_id && !dataroom.aws_region_id) {
            setShowRegionModal(true);
          } else {
            setActionLoading(true);
            // Use existing region for activation
            await dataroomService.activateDataroom(uuid, dataroom.region_id || dataroom.aws_region_id);
            toast.success('Dataroom activated successfully');
            fetchDataroomDetails();
          }
          break;
        case 'suspend':
          if (window.confirm('Are you sure you want to suspend this dataroom?')) {
            setActionLoading(true);
            // Use dataroomService for suspension
            await dataroomService.suspendDataroom(uuid, 'Suspended by admin');
            toast.success('Dataroom suspended successfully');
            fetchDataroomDetails();
          }
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this dataroom? This action cannot be undone.')) {
            
            // Try different possible endpoint patterns
            let deleteEndpoint = `/admin/datarooms/${uuid}`;
            
            try {
              const response = await api.delete(deleteEndpoint);
              toast.success('Dataroom deleted successfully');
              navigate('/dataroom-management');
              return;
            } catch (firstError) {
              
              // Try alternative endpoint pattern
              deleteEndpoint = `/admin/dataroom/${uuid}`;
              try {
                const response = await api.delete(deleteEndpoint);
                toast.success('Dataroom deleted successfully');
                navigate('/dataroom-management');
                return;
              } catch (secondError) {
                throw firstError; // Throw the original error
              }
            }
          }
          return; // Don't continue if user cancels
        default:
          break;
      }
    } catch (error) {
      setActionLoading(false);
      
      // More specific error handling
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || error.response.data?.error || 'Unknown error';
        
        switch (status) {
          case 404:
            toast.error('Dataroom not found');
            break;
          case 403:
            toast.error('You do not have permission to perform this action');
            break;
          case 422:
            toast.error(`Validation error: ${message}`);
            break;
          case 500:
            toast.error('Server error. Please try again later.');
            break;
          default:
            toast.error(`Failed to perform action: ${message}`);
        }
      } else if (error.request) {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error('Failed to perform action');
      }
    } finally {
      setActionLoading(false);
    }
  };


  const tabs = [
    { id: 'details', label: 'Details', icon: Building2 },
    { id: 'roles', label: 'Roles', icon: Shield },
    { id: 'members', label: 'Members', icon: Users },
    { id: 'tags', label: 'Tags', icon: Tag },
    { id: 'invitations', label: 'Invitations', icon: Mail }
  ];

  if (loading) {
    return <CommonLoader />;
  }

  if (!dataroom) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Dataroom not found</h2>
          <button
            onClick={() => navigate('/dataroom-management')}
            className="text-primary-600 hover:text-primary-700"
          >
            Back to Dataroom Management
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="px-4">
          <div className="py-4">
            {/* Top Row - Title and Back Button */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/dataroom-management')}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{dataroom.name}</h1>
                  <p className="text-sm text-gray-500 mt-1">
                    {dataroom.company_name}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Bottom Row - Status, Approval, and Actions */}
            <div className="flex items-center justify-between">
              {/* Left Side - Status */}
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-600">Status:</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  dataroom.status === 1
                    ? 'bg-green-100 text-green-700'
                    : dataroom.status === 2
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {dataroom.status_text}
                </span>
              </div>
              
              {/* Right Side - Approval and Actions */}
              <div className="flex items-center space-x-4">
                {/* Approval Status */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-600">Approval:</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    dataroom.approval_status === 1
                      ? 'bg-green-100 text-green-700'
                      : dataroom.approval_status === 0
                      ? 'bg-yellow-100 text-yellow-700'
                      : dataroom.approval_status === 2
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {dataroom.approval_status_text}
                  </span>
                </div>

                {/* Watermark Dropdown - Only show if is_owner is true */}
                {dataroom?.is_owner && (
                  <div className="relative watermark-dropdown">
                    <button
                      onClick={() => setShowWatermarkDropdown(!showWatermarkDropdown)}
                      disabled={watermarkLoading}
                      className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Droplets className="w-4 h-4 mr-2" />
                      {watermarkLoading ? 'Processing...' : 'Watermark'}
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </button>

                    {showWatermarkDropdown && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-10">
                        <div className="py-1">
                          <div className="px-3 py-1.5 text-xs text-gray-500 border-b">
                            Current: {dataroom.watermark_enabled ? 'Enabled' : 'Disabled'}
                          </div>
                          {!dataroom.watermark_enabled ? (
                            <button
                              onClick={() => handleWatermarkToggle(true)}
                              className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                              Enable Watermark
                            </button>
                          ) : (
                            <button
                              onClick={() => handleWatermarkToggle(false)}
                              className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                              Disable Watermark
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Actions Dropdown */}
                <div className="relative actions-dropdown">
                  <button
                    onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                    disabled={actionLoading}
                    className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading ? 'Processing...' : 'Actions'}
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </button>
                  
                  {showActionsDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-10">
                      <div className="py-1">
                        <button
                          onClick={() => handleDataroomAction('activate')}
                          disabled={dataroom.status === 'active' || dataroom.status === 1}
                          className={`flex items-center w-full px-3 py-1.5 text-sm ${
                            dataroom.status === 'active' || dataroom.status === 1
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <span className={`w-2 h-2 rounded-full mr-3 ${
                            dataroom.status === 'active' || dataroom.status === 1
                              ? 'bg-gray-400'
                              : 'bg-green-500'
                          }`}></span>
                          Activate Dataroom
                        </button>

                        <button
                          onClick={() => handleDataroomAction('suspend')}
                          disabled={dataroom.status === 'suspended' || dataroom.status === 2}
                          className={`flex items-center w-full px-3 py-1.5 text-sm ${
                            dataroom.status === 'suspended' || dataroom.status === 2
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <span className={`w-2 h-2 rounded-full mr-3 ${
                            dataroom.status === 'suspended' || dataroom.status === 2
                              ? 'bg-gray-400'
                              : 'bg-yellow-500'
                          }`}></span>
                          Suspend Dataroom
                        </button>
                        {/* Delete Dataroom - Commented out as requested */}
                        {/* <button
                          onClick={() => handleDataroomAction('delete')}
                          className="flex items-center w-full px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-3" />
                          Delete Dataroom
                        </button> */}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="px-4">
          <div className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-gray-50 py-6">
        <div className="px-4">
          <AnimatePresence mode="wait">
            {activeTab === 'details' && (
              <motion.div
                key="details"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                {/* Details Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Basic Information */}
                <div className="lg:col-span-2">
                  <div className="bg-white rounded-lg shadow">
                    <div className="p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-6">Basic Information</h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Dataroom Name</label>
                          <p className="text-sm text-gray-900 font-medium">{dataroom.name}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                          <p className="text-sm text-gray-900">{dataroom.company_name}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name</label>
                          <p className="text-sm text-gray-900">{dataroom.brand_name || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Organization Type</label>
                          <p className="text-sm text-gray-900">{dataroom.organization_type || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                          <p className="text-sm text-gray-900">{dataroom.industry || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Type</label>
                          <p className="text-sm text-gray-900">{dataroom.transaction_type || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="bg-white rounded-lg shadow mt-6">
                    <div className="p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-6">Contact Information</h2>

                      {/* Website */}
                      {dataroom.website && (
                        <div className="mb-6 pb-6 border-b border-gray-200">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            <Globe className="w-4 h-4 inline mr-1" />
                            Website
                          </label>
                          <a
                            href={dataroom.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {dataroom.website}
                          </a>
                        </div>
                      )}

                      {/* Primary Contact Person */}
                      <div className="mb-6 pb-6 border-b border-gray-200">
                        <h3 className="text-md font-medium text-gray-800 mb-4">Primary Contact Person</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                            <p className="text-sm text-gray-900">
                              {dataroom.primary_contact_name || 'N/A'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              <Phone className="w-4 h-4 inline mr-1" />
                              Contact Number
                            </label>
                            <p className="text-sm text-gray-900">
                              {dataroom.primary_contact_no || 'N/A'}
                            </p>
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              <Mail className="w-4 h-4 inline mr-1" />
                              Email ID
                            </label>
                            <p className="text-sm text-gray-900">
                              {dataroom.primary_contact_email || 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Secondary Contact Person */}
                      <div className="mb-6 pb-6 border-b border-gray-200">
                        <h3 className="text-md font-medium text-gray-800 mb-4">Secondary Contact Person</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                            <p className="text-sm text-gray-900">
                              {dataroom.secondary_contact_name || 'N/A'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              <Phone className="w-4 h-4 inline mr-1" />
                              Contact Number
                            </label>
                            <p className="text-sm text-gray-900">
                              {dataroom.secondary_contact_no || 'N/A'}
                            </p>
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              <Mail className="w-4 h-4 inline mr-1" />
                              Email ID
                            </label>
                            <p className="text-sm text-gray-900">
                              {dataroom.secondary_contact_email || 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Organization Email */}
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <Mail className="w-4 h-4 inline mr-1" />
                          Organization Email
                        </label>
                        <p className="text-sm text-gray-900">
                          {dataroom.organization_email || 'N/A'}
                        </p>
                      </div>

                      {/* Registered Office Address */}
                      {dataroom.registered_office_address && (
                        <div className="mt-6">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            <MapPin className="w-4 h-4 inline mr-1" />
                            Registered Office Address
                          </label>
                          <p className="text-sm text-gray-900">{dataroom.registered_office_address}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Compliance Information */}
                  <div className="bg-white rounded-lg shadow mt-6">
                    <div className="p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-6">Compliance Information</h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">CIN</label>
                          <p className="text-sm text-gray-900 font-mono">
                            {dataroom.corporate_identification_no || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">PAN</label>
                          <p className="text-sm text-gray-900 font-mono">
                            {dataroom.pan_number || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">TAN</label>
                          <p className="text-sm text-gray-900 font-mono">
                            {dataroom.tan_number || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
                          <p className="text-sm text-gray-900 font-mono">
                            {dataroom.gstin || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Statistics & Metrics */}
                <div className="space-y-6">
                  {/* Subscription Information */}
                  <div className="bg-white rounded-lg shadow">
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Details</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name</label>
                          <p className="text-sm font-medium text-gray-900">
                            {dataroom.pricing_plan?.name || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Plan Price</label>
                          <p className="text-sm text-gray-900">
                            ₹{dataroom.pricing_plan?.price || 0}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Storage Limit</label>
                          <p className="text-sm text-gray-900">
                            {dataroom.pricing_plan?.storage || 0} GB
                          </p>
                        </div>
                        {dataroom.subscription && (
                          <>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Status</label>
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                dataroom.subscription.status === 1 ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {dataroom.subscription.status_text}
                              </span>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Auto Renew</label>
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                dataroom.subscription.auto_renew ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {dataroom.subscription.auto_renew ? 'Enabled' : 'Disabled'}
                              </span>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Current Period</label>
                              <p className="text-xs text-gray-600">
                                {dataroom.subscription.current_period_starts_at ? 
                                  new Date(dataroom.subscription.current_period_starts_at).toLocaleDateString() : 'N/A'}
                                {' - '}
                                {dataroom.subscription.current_period_ends_at ? 
                                  new Date(dataroom.subscription.current_period_ends_at).toLocaleDateString() : 'N/A'}
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Important Dates */}
                  <div className="bg-white rounded-lg shadow">
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
                          <p className="text-sm text-gray-900">
                            {dataroom.created_at ? new Date(dataroom.created_at).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                        {dataroom.approved_at && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Approved</label>
                            <p className="text-sm text-gray-900">
                              {new Date(dataroom.approved_at).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                        {dataroom.updated_at && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                            <p className="text-sm text-gray-900">
                              {new Date(dataroom.updated_at).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Configuration */}
                  <div className="bg-white rounded-lg shadow">
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Configuration</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Watermark</label>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            dataroom.watermark_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {dataroom.watermark_enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </motion.div>
          )}

            {activeTab === 'roles' && (
              <DataroomRoles dataroomUuid={uuid} />
            )}

            {activeTab === 'tags' && (
              <DataroomTags dataroomUuid={uuid} />
            )}

            {activeTab === 'members' && (
              user?.role === 'super-admin' || user?.roles?.includes('super-admin') ? (
                <DataroomMembers dataroomUuid={uuid} />
              ) : (
                <DataroomMembersAdmin dataroomUuid={uuid} />
              )
            )}

            {activeTab === 'invitations' && (
              <DataroomInvitations dataroomUuid={uuid} />
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Region Selection Modal for Activation */}
      {showRegionModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <h3 className="text-lg font-semibold mb-4">Activate Dataroom</h3>
            <p className="text-gray-600 mb-4">
              You are about to activate the dataroom "{dataroom?.name}". 
              Please select the AWS region for deployment.
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                AWS Region <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                className="w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500"
              >
                <option value="">Select a region</option>
                {regions.map((region) => (
                  <option key={region.id} value={region.id}>
                    {region.name} ({region.code})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRegionModal(false);
                  setSelectedRegion('');
                }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={confirmRegionAndActivate}
                disabled={!selectedRegion || actionLoading}
                className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {actionLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Activating...
                  </>
                ) : (
                  'Activate Dataroom'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

    </div>
  );
};

export default DataroomDetail;